// Demo Lens API: serves the Foreplay (Demo) Lens from local data so the
// Launchpad demo reports work on any plan. Ad rows come from Foreplay's own
// ads (lensDemoData.js); report edits are kept in memory.
import { nanoid } from 'nanoid'
import {
  DEMO_LENS_ID,
  DEMO_REPORT_IDS,
  DEMO_FOLDER_ID,
  DEMO_PRESET_ID,
  DEMO_PRESET_COLUMNS_HASH,
  DEMO_AD_ACCOUNTS,
  DEMO_BUSINESS_IDS
} from '@/utils/lens/demo/lensDemoConstants'
import {
  loadDemoAdRows,
  getCachedDemoAdRows,
  queryDemoGroups,
  queryDemoGroupAds,
  queryDemoCreative,
  buildDemoUniqueMetrics,
  buildDemoAdComments,
  buildDemoTimeSeries,
  buildDemoInsights,
  buildDefaultComparisonGroups
} from '@/utils/lens/demo/lensDemoData'

// Default columns for the demo reports and preset: Foreplay Scores, then the
// performance / conversion / engagement / media metrics teams look at first
const DEMO_TABLE_COLUMNS = [
  'spend', 'percent_of_spend', 'roas', 'cpa', 'total_conversions', 'conversion_value',
  'hook_score', 'watch_score', 'click_score', 'convert_score',
  'ctr', 'link_ctr', 'cpm', 'thumbstop_ratio', 'hook_rate', 'hold_rate'
].map((key) => ({ key, is_pinned: false }))

const DEMO_TABLE_COLUMN_KEYS = DEMO_TABLE_COLUMNS.map((col) => col.key)

// In-memory store: demo report edits live for the browser session only
const reportStore = {}
let createdReportCount = 0

function nowMs () {
  return Date.now()
}

function hoursAgoIso (hours) {
  return new Date(Date.now() - hours * 60 * 60 * 1000).toISOString()
}

function buildBaseReport (overrides = {}) {
  return {
    lens_id: DEMO_LENS_ID,
    folder_id: DEMO_FOLDER_ID,
    description: '',
    group_by: 'ad_name',
    graph_type: 'bar',
    click_attribution_window: '7d',
    view_attribution_window: '1d',
    selected_ad_accounts: [DEMO_AD_ACCOUNTS.META_US, DEMO_AD_ACCOUNTS.META_PROSPECTING],
    filters: [
      [{ key: 'event_date', operation: 'greater_than', value: 'last_7_days' }],
      [{ key: 'event_date', operation: 'less_than', value: 'today' }]
    ],
    table_columns: DEMO_TABLE_COLUMNS,
    selected_columns: {
      bar: ['spend', 'impressions', 'roas'],
      grid: ['roas', 'spend', 'percent_of_spend', 'total_conversions'],
      line: ['spend', 'roas']
    },
    selected_graph_rows: {
      bar: 4,
      line: 4,
      grid: 4
    },
    sorted_column: { by: 'spend', order: 'desc' },
    preset_id: DEMO_PRESET_ID,
    preset: { id: DEMO_PRESET_ID },
    columns_hash: DEMO_PRESET_COLUMNS_HASH,
    custom_groups_filters: [],
    table_config: {
      colorFormat: 'positive',
      showStatus: true,
      showTags: false,
      showCreationRange: false,
      showWeightedAvg: true,
      showSummation: true
    },
    created_at: nowMs() - 4 * 24 * 60 * 60 * 1000,
    updated_at: nowMs() - 2 * 60 * 60 * 1000,
    tiktok_config: {
      columns_hash: DEMO_PRESET_COLUMNS_HASH,
      table_columns: DEMO_TABLE_COLUMNS,
      selected_columns: {
        bar: ['spend', 'impressions', 'roas'],
        grid: ['roas', 'spend', 'percent_of_spend', 'total_conversions'],
        line: ['spend', 'roas']
      },
      sorted_column: { by: 'spend', order: 'desc' },
      preset_id: DEMO_PRESET_ID,
      preset: { id: DEMO_PRESET_ID },
      filters: [
        [{ key: 'event_date', operation: 'greater_than', value: 'last_7_days' }],
        [{ key: 'event_date', operation: 'less_than', value: 'today' }]
      ],
      custom_groups_filters: []
    },
    ...overrides
  }
}

function seedReports () {
  if (!reportStore[DEMO_REPORT_IDS.TOP_PERFORMING]) {
    reportStore[DEMO_REPORT_IDS.TOP_PERFORMING] = buildBaseReport({
      id: DEMO_REPORT_IDS.TOP_PERFORMING,
      name: 'Top Performing Ads',
      type: 'top-performing',
      description: 'Foreplay demo — currently running ads ranked by spend.',
      graph_type: 'bar'
    })
  }
  if (!reportStore[DEMO_REPORT_IDS.COMPARATIVE]) {
    reportStore[DEMO_REPORT_IDS.COMPARATIVE] = buildBaseReport({
      id: DEMO_REPORT_IDS.COMPARATIVE,
      name: 'Agency, Creators, and In-house',
      type: 'comparative',
      description: 'Foreplay demo — three groups by ad name: contains "agency", contains "creator", and neither.',
      graph_type: 'bar',
      custom_groups_filters: buildDefaultComparisonGroups()
    })
    reportStore[DEMO_REPORT_IDS.COMPARATIVE].tiktok_config = {
      ...reportStore[DEMO_REPORT_IDS.COMPARATIVE].tiktok_config,
      custom_groups_filters: buildDefaultComparisonGroups()
    }
  }
}

seedReports()

export function getDemoAdAccounts () {
  return [
    {
      account_id: DEMO_AD_ACCOUNTS.META_US,
      account_name: 'Foreplay US',
      provider: 'facebook',
      business_id: DEMO_BUSINESS_IDS.META,
      is_valid: true
    },
    {
      account_id: DEMO_AD_ACCOUNTS.META_PROSPECTING,
      account_name: 'Foreplay Prospecting',
      provider: 'facebook',
      business_id: DEMO_BUSINESS_IDS.META,
      is_valid: true
    },
    {
      account_id: DEMO_AD_ACCOUNTS.TIKTOK,
      account_name: 'Foreplay TikTok',
      provider: 'tiktok',
      business_id: DEMO_BUSINESS_IDS.TIKTOK,
      is_valid: true
    }
  ]
}

export function getDemoProviders () {
  return [
    {
      provider: 'facebook',
      business_id: DEMO_BUSINESS_IDS.META,
      business_name: 'Foreplay Meta',
      is_valid: true,
      email: 'demo@foreplay.co',
      user_id: 'demo-user',
      access_type: 'owner',
      advertisers: [
        { id: DEMO_AD_ACCOUNTS.META_US, name: 'Foreplay US', currency: 'USD' },
        { id: DEMO_AD_ACCOUNTS.META_PROSPECTING, name: 'Foreplay Prospecting', currency: 'USD' }
      ]
    },
    {
      provider: 'tiktok',
      business_id: DEMO_BUSINESS_IDS.TIKTOK,
      business_name: 'Foreplay TikTok',
      is_valid: true,
      email: 'demo@foreplay.co',
      user_id: 'demo-user',
      access_type: 'owner',
      advertisers: [
        { id: DEMO_AD_ACCOUNTS.TIKTOK, name: 'Foreplay TikTok', currency: 'USD' }
      ]
    }
  ]
}

export function getDemoLens () {
  return {
    id: DEMO_LENS_ID,
    name: 'Foreplay (Demo)',
    avatar: null,
    currency: 'USD',
    timezone: 'America/New_York',
    click_attribution_window: '7d',
    view_attribution_window: '1d',
    goal_metric: 'roas',
    tiktok_goal_metric: 'roas',
    last_refreshed_at: hoursAgoIso(2),
    ad_accounts: getDemoAdAccounts(),
    metadata: {
      min_event_date: new Date(Date.now() - 180 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10)
    }
  }
}

function getDemoFolderReports () {
  return Object.values(reportStore).map((report) => ({
    id: report.id,
    name: report.name,
    type: report.type,
    description: report.description
  }))
}

export function getDemoSidebarLens () {
  return {
    ...getDemoLens(),
    folders: [
      {
        id: DEMO_FOLDER_ID,
        name: 'Default',
        reports: getDemoFolderReports()
      }
    ],
    isDisconnected: false,
    isDataReady: true,
    accessType: 'owner'
  }
}

export function getDemoLensStatus () {
  return {
    lens_id: DEMO_LENS_ID,
    access_type: 'owner',
    ad_accounts: [
      { business_id: DEMO_BUSINESS_IDS.META, is_valid: true, data_accuracy: { percent_diff: 1 } },
      { business_id: DEMO_BUSINESS_IDS.TIKTOK, is_valid: true, data_accuracy: { percent_diff: 1 } }
    ]
  }
}

async function withRows () {
  try {
    return await loadDemoAdRows()
  } catch (error) {
    console.warn('[LensDemo] Falling back to cached/snapshot rows', error)
    return getCachedDemoAdRows()
  }
}

function listReports () {
  seedReports()
  return Object.values(reportStore)
}

const Lens = {
  async get () {
    return { data: getDemoLens() }
  },
  async getInsightByLens () {
    const rows = await withRows()
    return { data: buildDemoInsights(rows) }
  },
  async getAdComments (lensId, adId) {
    return { data: buildDemoAdComments(adId) }
  }
}

const LensReport = {
  async getByID (reportId) {
    seedReports()
    const report = reportStore[reportId]
    if (!report) {
      return { data: {} }
    }
    return {
      data: { ...report }
    }
  },
  async getAll (query = {}) {
    const reports = listReports().filter((report) => {
      if (query.lens_id && report.lens_id !== query.lens_id) return false
      if (query.report_type && report.type !== query.report_type) return false
      if (query.name && !String(report.name).toLowerCase().includes(String(query.name).toLowerCase())) return false
      return true
    })
    const page = Number(query.page) || 1
    const limit = Number(query.limit) || reports.length || 10
    const start = (page - 1) * limit
    const pageRows = reports.slice(start, start + limit)
    return {
      data: pageRows.map((report) => ({ ...report })),
      pages: Math.max(1, Math.ceil(reports.length / limit)),
      total: reports.length
    }
  },
  async create (reportPayload = {}) {
    createdReportCount += 1
    const id = `demo-report-${nanoid(8)}`
    const report = buildBaseReport({
      ...reportPayload,
      id,
      name: reportPayload.name || `Demo Report ${createdReportCount}`,
      type: reportPayload.type || 'top-performing',
      created_at: nowMs(),
      updated_at: nowMs()
    })
    reportStore[id] = report
    return { data: report }
  },
  async update (reportId, toUpdate = {}) {
    seedReports()
    const current = reportStore[reportId]
    if (!current) return { data: {} }
    reportStore[reportId] = {
      ...current,
      ...toUpdate,
      id: reportId,
      updated_at: nowMs()
    }
    return { data: reportStore[reportId] }
  }
}

const AdGroups = {
  async getLensGroups (lensId, query, reportType) {
    const rows = await withRows()
    return queryDemoGroups(rows, query, reportType)
  },
  async getLensGroupAds (lensId, query, reportType) {
    const rows = await withRows()
    return queryDemoGroupAds(rows, query, reportType)
  },
  async getLensCreativeGroup (idPayload, query) {
    const rows = await withRows()
    return queryDemoCreative(rows, idPayload, query)
  }
}

const TimeSeries = {
  async getGroupTimeSeriesData (lensId, query, interval, isComparative) {
    const rows = await withRows()
    return buildDemoTimeSeries(rows, query, interval, isComparative)
  }
}

function buildPreset (platform) {
  return {
    id: DEMO_PRESET_ID,
    name: 'Demo Default',
    is_default: true,
    provider: platform === 'tiktok' ? 'tiktok' : 'facebook',
    columns_hash: DEMO_PRESET_COLUMNS_HASH,
    table_columns: DEMO_TABLE_COLUMN_KEYS,
    lens_specific_columns: {}
  }
}

// Presets saved on the demo Lens live in memory for the session, so the
// tour's "save a preset" works without writing to the user's real account
let savedPresets = []
const presetPlatform = (preset) => (preset.provider === 'tiktok' ? 'tiktok' : 'meta')

const LensPreset = {
  async getAll () {
    const saved = (platform) => savedPresets.filter((preset) => presetPlatform(preset) === platform)
    return {
      data: {
        meta: [buildPreset('meta'), ...saved('meta')],
        tiktok: [buildPreset('tiktok'), ...saved('tiktok')]
      }
    }
  },
  async create (payload) {
    const preset = { lens_specific_columns: {}, ...payload, id: `demo-preset-${Date.now()}`, is_default: false }
    savedPresets.push(preset)
    return { data: preset }
  },
  // Edits to the seeded default only apply locally (MetricsModule keeps them)
  async update (presetId, payload) {
    savedPresets = savedPresets.map((preset) => (preset.id === presetId ? { ...preset, ...payload, id: presetId } : preset))
    return { data: {} }
  },
  async remove (presetId) {
    if (presetId === DEMO_PRESET_ID) throw new Error("The demo preset can't be deleted")
    savedPresets = savedPresets.filter((preset) => preset.id !== presetId)
    return { data: {} }
  }
}

const emptyTags = async () => ({ data: [] })

// Demo tags are read-only: nothing to list, writes are no-ops
const Tags = {
  getAll: emptyTags,
  getTagsByCreativeHash: emptyTags,
  create: emptyTags,
  tagCreative: emptyTags,
  unTagCreative: emptyTags
}

const Ads = {
  async getAdUniqueMetrics (lensId, adAccountId, query = {}) {
    const rows = await withRows()
    const adId = query.adIds?.[0]
    return { data: buildDemoUniqueMetrics(rows.find((row) => row.ad_id === adId)) }
  }
}

// Demo stand-ins for the Lens request modules. src/api/lens/index.js routes
// calls here when they target the demo Lens / report ids.
export default {
  Lens,
  LensReport,
  AdGroups,
  Ads,
  TimeSeries,
  LensPreset,
  Tags
}
